import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePatients, useDeletePatient } from '@/hooks/api'
import { PATIENTS_PER_PAGE } from './PatientsPage.static'
import type { GetPatientsParams } from '@/services/api/patients/patient.static'

export type SortOption = 'name_asc' | 'name_desc' | 'admissionDate_asc' | 'admissionDate_desc'

export function usePatientsPage() {
  const [page, setPage] = useState(1)
  const [inputValue, setInputValue] = useState('')
  const [search, setSearch] = useState('')
  const [showArchive, setShowArchive] = useState(false)
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null)
  const [sort, setSort] = useState<SortOption | ''>('')
  const { t } = useTranslation('patients')
  const deletePatientMutation = useDeletePatient()

  const handleSearch = () => {
    setSearch(inputValue)
    setPage(1)
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleClearSearch = () => {
    setInputValue('')
    setSearch('')
    setPage(1)
  }

  const [sortBy, sortOrder] = sort
    ? (sort.split('_') as ['name' | 'admissionDate', 'asc' | 'desc'])
    : [undefined, undefined]

  const params: GetPatientsParams = {
    page,
    limit: PATIENTS_PER_PAGE,
    ...(search && { search }),
    ...(showArchive ? { archived: true } : { status: 'ACTIVE' as const }),
    ...(sortBy && { sortBy, sortOrder }),
  }

  const { data, isLoading, isError, refetch } = usePatients(params)

  const patients = data?.patients ?? []
  const total = data?.total ?? 0
  const totalPages = Math.ceil(total / PATIENTS_PER_PAGE)
  const isEmpty = !isLoading && patients.length === 0

  const handleSetSort = (val: SortOption | '') => {
    setSort(val)
    setPage(1)
  }

  const handleToggleArchive = () => {
    setShowArchive((prev) => !prev)
    setPage(1)
  }

  const handleDeletePatient = () => {
    if (!deleteTargetId) return
    deletePatientMutation.mutate(deleteTargetId, {
      onSettled: () => setDeleteTargetId(null),
    })
  }

  return {
    t,
    patients,
    total,
    page,
    totalPages,
    showArchive,
    inputValue,
    sort,
    isLoading,
    isError,
    isEmpty,
    deleteTargetId,
    refetch,
    setInputValue,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,
    setPage,
    handleSetSort,
    handleToggleArchive,
    setDeleteTargetId,
    handleDeletePatient,
  }
}
